import type { FunctionComponent } from 'react'
import { useMemo, useEffect, useState } from 'react'
import { SelectField } from '@retailer-platform/shared-components/src/tds'
import { useWarehouseLocations } from '../../../legacy/common/utils/warehouse-locations/locations.hooks'
import type { WarehouseLocation } from '../../../legacy/common/types'

/**
 * Props for the StoreSelect component
 */
interface StoreSelectProps {
  /** The currently selected store ID */
  value: string
  /** Callback invoked when the selected store changes */
  onChange: (value: string) => void
  /** Placeholder text displayed when no store is selected */
  placeholder?: string
  /** Whether the select field is disabled */
  isDisabled?: boolean
  /**
   * Optional async filter function to limit which stores are displayed in the select.
   *
   * The filter function receives all available stores and should return a Promise
   * that resolves to an array of store IDs to display. The final list of stores
   * shown in the select is the intersection of the base stores and the filtered IDs.
   *
   * **Execution timing:**
   * - Executes on component mount (if stores are loaded)
   * - Re-executes when the filter function reference changes
   * - Does NOT execute if no stores are available
   *
   * **Loading state:**
   * - The component shows a loading state while the filter executes
   * - The loading state combines both data fetching and filter execution
   *
   * **Error handling:**
   * - If the filter throws an error, the select becomes disabled
   * - The error is captured and stored in component state
   *
   * @param stores - Array of all available WarehouseLocation objects
   * @returns Promise resolving to an array of store IDs to display
   *
   * @example
   * // Filter to show only stores with pickup enabled
   * const pickupFilter = async (stores) => {
   *   return stores.filter(store => store.pickup).map(store => store.id)
   * }
   *
   * @example
   * // Async filter that fetches allowed store IDs from an API
   * const apiFilter = async (stores) => {
   *   const response = await fetch('/api/allowed-stores')
   *   const allowedIds = await response.json()
   *   // Return intersection of available stores and allowed stores
   *   return stores
   *     .filter(store => allowedIds.includes(store.id))
   *     .map(store => store.id)
   * }
   *
   * @example
   * // Filter based on store location
   * const locationFilter = async (stores) => {
   *   return stores
   *     .filter(store => store.state === 'CA')
   *     .map(store => store.id)
   * }
   */
  filter?: (stores: WarehouseLocation[]) => Promise<string[]>
}

/**
 * StoreSelect Component
 *
 * A select field for choosing a store (retailer location) from a list of available stores.
 * Fetches stores automatically and supports optional async filtering to limit displayed options.
 *
 * @example
 * // Basic usage
 * <StoreSelect
 *   value={selectedStoreId}
 *   onChange={setSelectedStoreId}
 *   placeholder="Select a store"
 * />
 *
 * @example
 * // With filter
 * <StoreSelect
 *   value={selectedStoreId}
 *   onChange={setSelectedStoreId}
 *   placeholder="Select a store"
 *   filter={async (stores) => {
 *     const allowedIds = await fetchAllowedStores()
 *     return stores
 *       .filter(store => allowedIds.includes(store.id))
 *       .map(store => store.id)
 *   }}
 * />
 */
export const StoreSelect: FunctionComponent<StoreSelectProps> = ({
  value,
  onChange,
  placeholder,
  isDisabled = false,
  filter,
}) => {
  const { data: stores, loading: storesLoading, error: storesError } = useWarehouseLocations()

  const [filterLoading, setFilterLoading] = useState(false)
  const [filterError, setFilterError] = useState<Error | null>(null)
  const [filteredStoreIds, setFilteredStoreIds] = useState<string[] | null>(null)

  // Execute filter function when it changes or stores are loaded
  useEffect(() => {
    if (!filter || !stores || stores.length === 0) {
      setFilteredStoreIds(null)
      setFilterError(null)
      return
    }

    let isCancelled = false

    const executeFilter = async () => {
      setFilterLoading(true)
      setFilterError(null)

      try {
        const result = await filter(stores)
        if (!isCancelled) {
          setFilteredStoreIds(result)
          setFilterLoading(false)
        }
      } catch (error) {
        if (!isCancelled) {
          setFilterError(error instanceof Error ? error : new Error('Filter execution failed'))
          setFilterLoading(false)
        }
      }
    }

    executeFilter()

    return () => {
      isCancelled = true
    }
  }, [filter, stores])

  // Compute final stores list (intersected with filter if provided)
  const finalStores = useMemo(() => {
    if (!stores) return []
    if (!filteredStoreIds) return stores

    const filteredIdsSet = new Set(filteredStoreIds)
    return stores.filter(store => filteredIdsSet.has(store.id))
  }, [stores, filteredStoreIds])

  const storeOptions = useMemo(
    () =>
      finalStores.map(store => ({
        label: store.name || `Unnamed Store ${store.id}`,
        value: store.id,
      })),
    [finalStores]
  )

  const loading = storesLoading || filterLoading
  const error = storesError || filterError

  return (
    <SelectField
      options={storeOptions}
      placeholder={placeholder}
      value={value || null}
      onChange={(newValue?: string) => onChange(newValue || '')}
      isLoading={loading}
      searchable
      disabled={isDisabled || !!error}
    />
  )
}
